import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription, catchError, of } from 'rxjs';

import { LocaleLinkPipe } from 'src/app/Shared/_pipes/locale-link/locale-link.pipe';

import { InternationalAuctionsService } from '../../_services/international-auctions.service';
import { ExternalSaleView } from '../../pages/international-auctions/_mock/international-auctions.models';
import { InternationalSaleCardComponent } from '../../pages/international-auctions/_shared/sale-card/international-sale-card.component';

/** The `window` hook this component installs in the browser. Prototype only. */
const TOGGLE_HOOK = '__bxsToggleIABand';

/**
 * International Auctions — the homepage band.
 *
 * The `isHome` state of the approved mockup
 * (`docs/external-cataloge/Bidxsell co-branded catalogue/.../International
 * Auctions.dc.html`): eyebrow / title / standfirst with a "View all" outline
 * button beside them, the live counter line, a horizontally scrolling rail of
 * partner sales, and the preview-only disclaimer.
 *
 * THREE THINGS THIS COMPONENT IS DELIBERATE ABOUT
 * -----------------------------------------------
 * 1. It renders NOTHING when the admin flag is off. `@if` wraps the whole
 *    template, so the off state leaves no wrapper and no padding behind — the
 *    homepage closes up exactly as if the band had never been imported. A
 *    `display: none` on a still-present host would still cost a DOM subtree and
 *    would still ship the cards' images to the client.
 *
 * 2. It renders nothing when it has no sales either. This is the most-viewed
 *    page on the platform; a band that is present but empty is worse than a
 *    band that is absent, and it doubles as the error state — every stream is
 *    guarded with `catchError`, so a failing service degrades to "no band"
 *    rather than to a thrown error that takes the homepage down with it.
 *
 * 3. It never re-sorts. `listSales()` returns live → upcoming → preview →
 *    closed already, which is the order the mockup's rail reads in, and the
 *    service is the single place that ordering is allowed to be decided.
 *
 * Cards come from the shared `app-international-sale-card` in `variant="rail"`;
 * that component owns its own fixed width, its 82vw mobile width and its
 * `scroll-snap-align`, so the rail here only has to turn snapping ON below
 * 900px.
 *
 * Ticket: BXS-3627 (F1.5)
 */
@Component({
  selector: 'app-international-auctions-band',
  standalone: true,
  imports: [
    RouterLink,
    TranslatePipe,
    LocaleLinkPipe,
    InternationalSaleCardComponent,
  ],
  templateUrl: './international-auctions-band.component.html',
  styleUrl: './international-auctions-band.component.scss',
})
export class InternationalAuctionsBandComponent implements OnInit, OnDestroy {
  /** The admin flag. Starts `false`, so nothing renders before it answers. */
  enabled = false;

  /** Already in display order — see the class comment. */
  sales: ExternalSaleView[] = [];

  liveCount = 0;

  /** "Upcoming" in the counter line means upcoming + preview, as the mockup. */
  upcomingCount = 0;

  private readonly subscriptions = new Subscription();
  private readonly isBrowser: boolean;

  constructor(
    private readonly service: InternationalAuctionsService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    // Subscribed rather than read once: `isHomepageBandEnabled()` is backed by a
    // BehaviorSubject that stays open, so flipping the flag re-renders a
    // homepage that is already on screen.
    this.subscriptions.add(
      this.service
        .isHomepageBandEnabled()
        .pipe(catchError(() => of(false)))
        .subscribe((enabled) => {
          this.enabled = enabled;
          this.cdr.markForCheck();
        })
    );

    this.subscriptions.add(
      this.service
        .listSales()
        .pipe(catchError(() => of<ExternalSaleView[]>([])))
        .subscribe((sales) => {
          this.sales = Array.isArray(sales) ? sales : [];
          this.cdr.markForCheck();
        })
    );

    this.subscriptions.add(
      this.service
        .getSaleCounts()
        .pipe(catchError(() => of(null)))
        .subscribe((counts) => {
          this.liveCount = counts?.live ?? 0;
          this.upcomingCount = (counts?.upcoming ?? 0) + (counts?.preview ?? 0);
          this.cdr.markForCheck();
        })
    );

    // PROTOTYPE ONLY — remove when the real config key lands.
    // `config_key_values` → `international_auctions_homepage_band_enabled` has
    // no admin screen yet, so this is how both states of the band get
    // demonstrated: `__bxsToggleIABand(false)` from the console hides it live.
    if (isPlatformBrowser(this.platformId)) {
      (window as any)[TOGGLE_HOOK] = (v: boolean) =>
        this.service.setHomepageBandEnabled(v);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    // PROTOTYPE ONLY — goes with the hook installed above.
    if (this.isBrowser) {
      delete (window as any)[TOGGLE_HOOK];
    }
  }

  /**
   * `0 / 1 / n` pick their own sentence rather than sharing one with a count
   * placeholder — Arabic has no single plural form that would cover all three.
   */
  get liveCounterKey(): string {
    if (this.liveCount <= 0) {
      return 'international_auctions.counter.live_none';
    }
    return this.liveCount === 1
      ? 'international_auctions.counter.live_one'
      : 'international_auctions.counter.live_other';
  }

  /** The band is a whole section; it is present or it is not. */
  get show(): boolean {
    return this.enabled && this.sales.length > 0;
  }
}
